import { track } from '@shared/events';
import {
	createContext,
	memo,
	Dispatch,
	SetStateAction,
	Suspense,
	useContext,
	useMemo,
	useEffect,
	useState,
} from 'react';
import { Link, useNavigate } from '@tanstack/react-router';

import config from '@/config';
import {
	useIssues,
	useNavbarIconUrl,
	useResponsiveGridBreakpoint,
	useRowVirtualizer,
} from '@shared/lib/hooks';
import { TemplatePoets } from '@shared/config';
import { Article, Issue } from '@/constants';
import {
	Dialog,
	DialogContent,
	dialogCardBackdropClassName,
	dialogCardClassName,
} from '@shared/ui/dialog';
import { ErrorBoundary } from '@shared/ui/error-boundary';

import { useOfflineCover } from '@entities/issue';
import { useIsSupportMagazine } from './magazines.hooks';
import { HomeContext } from '../home.context';
import { useIssue } from '@pages/issue/model/issue.hooks';
import { PreviewLink, previewTransitionName } from '@shared/ui/preview-link';
import { Skeleton } from '@shared/ui/skeleton';

import styles from './magazines.module.css';
import { T, useT } from '@shared/i18n';

export const Magazines = () => {
	const [isSubscriptionDialogOpen, setIsSubscriptionDialogOpen] =
		useState(false);

	const isDigitalAccess = useIsSupportMagazine();
	const { selectedIssue } = useContext(HomeContext);

	const { issues, isLoading, error } = useIssues();
	// useSelectIssue(issues);
	useEffect(() => {
		if (!selectedIssue) {
			document.title = config.build.title;
			return;
		}
		if (!selectedIssue.name) {
			return;
		}
		document.title = selectedIssue.name;
	}, [selectedIssue]);

	// Above the `isLoading` / `selectedIssue` early returns below -- hooks
	// can't be called after a conditional return.
	const contextValue = useMemo(
		() => ({
			isDigitalAccess,
			setIsSubscriptionDialogOpen,
		}),
		[isDigitalAccess],
	);

	if (isLoading) {
		// Not `styles['loading']` -- that class is `display: flex;
		// align-items: center; justify-content: center`, built for the old
		// single centered-spinner loading state. A flex-centered container
		// shrink-wraps its child to its own intrinsic size rather than
		// stretching it to fill the section, so it silently squeezed this
		// content-shaped skeleton down to a fraction of the real grid's
		// width (every clamp()/percentage inside it then resolved against
		// that wrong, narrower space too). The real (loaded) `<section>`
		// below isn't flex at all -- plain block layout is what this needs.
		return (
			<section className={styles['magazines']}>
				<MagazineLoading />
			</section>
		);
	}
	if (error && !issues.length) {
		// Matches morningstar's `Magazines()` (sections.hooks.ts's
		// `useSection`, same error-surfacing contract as `useIssues` here):
		// without this, a genuinely failed fetch (e.g. offline with nothing
		// usable in the persisted cache yet) fell through to the grid below
		// with an empty `issues` array -- a blank, content-shaped section
		// with no cards and no explanation, easy to mistake for "still
		// loading" rather than "couldn't load." `!issues.length` only: a
		// background refetch error on an already-populated (cached) list
		// shouldn't blank out content the reader can still see and use.
		return (
			<section className={styles['magazines']}>
				<div className={styles['error']}>{error.message}</div>
			</section>
		);
	}
	if (selectedIssue) {
		return (
			<section
				className={styles['magazines']}
				style={{ width: '100%', height: '100%' }}
			>
				{/* useIssue() here isn't route-loader-backed (this is a home-page
				    issue picker, not the /issues/$id route) -- topic 08's
				    useSuspenseQuery conversion means selecting an issue genuinely
				    suspends while it fetches, so it needs its own local boundary
				    rather than bubbling up to blank the whole home page.
				    `Suspense` alone only covers the *pending* case -- when the
				    fetch ultimately rejects (offline, and this specific issue
				    isn't cached), `useSuspenseQuery` throws for real, which a
				    `Suspense` boundary doesn't catch. Without the `ErrorBoundary`
				    below, that throw was uncaught here and bubbled all the way
				    up to the *route's* own error boundary -- home's `RouteError`,
				    the full-page "You're offline" screen -- even though this is
				    still the home route and the reader had already been shown
				    the (still-cached) issues grid a moment earlier. Reproduced on
				    a real offline device, 2026-09-02. */}
				<ErrorBoundary
					// i18n-ignore: an error-reporting id, not a label
					label="poets-cover-issue"
					resetKeys={[selectedIssue.id]}
					fallback={<CoverIssueUnavailable />}
				>
					<Suspense fallback={<MagazineLoading />}>
						<CoverIssue />
					</Suspense>
				</ErrorBoundary>
			</section>
		);
	}

	return (
		<MagazinesContext.Provider value={contextValue}>
			<section className={styles['magazines']}>
				<Banner />
				<IssuesContainer issues={issues} />
				<SubscritionDialog isOpen={isSubscriptionDialogOpen} />
			</section>
		</MagazinesContext.Provider>
	);
};

const MagazinesContext = createContext<MagazinesContextProps>({
	isDigitalAccess: null,
	setIsSubscriptionDialogOpen: () => {},
});

interface MagazinesContextProps {
	isDigitalAccess: boolean | null;
	setIsSubscriptionDialogOpen: Dispatch<SetStateAction<boolean>>;
}

function Banner() {
	const [isLoaded, setIsLoaded] = useState(false);
	setTimeout(() => {
		setIsLoaded(true);
	}, 10000);
	return (
		<div className={styles['banner']}>
			<ins
				data-revive-zoneid="63"
				data-revive-id="fbf717dc1969dc79e517b3994cd0df44"
			></ins>
			{isLoaded ? (
				<script
					async
					src="https://fast.poets-writers.org/openx/www/delivery/asyncjs.php"
				></script>
			) : null}
		</div>
	);
}

export const IssuesContainer = ({ issues }: IssuesProps) => {
	if (!issues) {
		return null;
	}
	const latestIssue = issues[0];
	return (
		<div className={styles['issues-container']}>
			<div>
				{latestIssue ? <LatestIssue issue={latestIssue} /> : null}
			</div>
			<div>
				<Issues issues={issues} />
			</div>
		</div>
	);
};

const LatestIssue = ({ issue }: { issue: Issue }) => {
	const t = useT();
	const { isDigitalAccess, setIsSubscriptionDialogOpen } =
		useContext(MagazinesContext);
	// `description` is deliberately not read here: this card has never
	// rendered one. The previous code blanked `issue.description` during
	// render and then tested the blanked value, so the `<p>` below was
	// dead code -- while the write itself corrupted the shared object.
	const { id, name, cover, slug } = issue;
	const contentProtection = config?.app?.contentProtection;
	const template = config.app.template as TemplatePoets;
	// Section 08's cover-morph generalization: both branches have a
	// receiving-side cover to morph into -- `/preview/$id` (`pages/preview`'s
	// `Cover()`) and, for `skipTOC` tenants, `/issues/$id` (the reader's
	// `IssueCover`), same as `basic`'s equivalent branches.
	const skipTOC = !!template?.config?.skipTOC;
	const from = `${window.location.pathname}?section=magazines`;
	const transitionName = previewTransitionName(slug ?? id);
	// Greyed out offline when the page this cover opens has nothing cached
	// (the reader with `skipTOC`, the preview screen without) -- see
	// `useOfflineCover` (`@entities/issue`). Not for readers without digital
	// access: their cover opens the subscription dialog, not an issue.
	const { coverStyle: coverFilterStyle, blockIfUnavailable } =
		useOfflineCover(slug ?? id, skipTOC ? 'issue' : 'preview', {
			enabled: !!isDigitalAccess,
		});
	const onClick = (event: React.MouseEvent) => {
		blockIfUnavailable(event);
	};

	const imageComponent = isDigitalAccess ? (
		skipTOC ? (
			<PreviewLink
				to="/issues/$id"
				params={{ id: `${slug ?? id}` }}
				from={from}
				onClick={onClick}
				// Every template's covers opt out of the router's hover/
				// touchstart prefetch: it would cache issues never actually
				// opened, and "not greyed out" (`useOfflineCover`) would stop
				// meaning "readable offline". The click still fetches normally.
				preload={false}
				// The cover morphs into the reader's own cover
				// (`widgets/issue-reader`'s `IssueCover`), same as basic's
				// `skipTOC` branch.
				payload={{ preview: { imageSrc: cover, transitionName } }}
			>
				<figure>
					{contentProtection ? (
						// eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions
						<img
							src={cover}
							alt={t('common.coverOf', { name: name })}
							width={340}
							fetchPriority="high"
							className="content-protection"
							onContextMenu={(e) => e.preventDefault()}
							style={{
								viewTransitionName: transitionName,
								...coverFilterStyle,
							}}
						/>
					) : (
						<img
							src={cover}
							alt={t('common.coverOf', { name: name })}
							width={340}
							fetchPriority="high"
							style={{
								viewTransitionName: transitionName,
								...coverFilterStyle,
							}}
						/>
					)}
				</figure>
			</PreviewLink>
		) : (
			<PreviewLink
				to="/preview/$id"
				params={{ id: `${slug ?? id}` }}
				from={from}
				onClick={onClick}
				preload={false}
				payload={{ preview: { imageSrc: cover, transitionName } }}
			>
				<figure>
					{contentProtection ? (
						// eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions
						<img
							src={cover}
							alt={t('common.coverOf', { name: name })}
							width={340}
							fetchPriority="high"
							style={{
								viewTransitionName: transitionName,
								...coverFilterStyle,
							}}
							className="content-protection"
							onContextMenu={(e) => e.preventDefault()}
						/>
					) : (
						<img
							src={cover}
							alt={t('common.coverOf', { name: name })}
							width={340}
							fetchPriority="high"
							style={{
								viewTransitionName: transitionName,
								...coverFilterStyle,
							}}
						/>
					)}
				</figure>
			</PreviewLink>
		)
	) : (
		// eslint-disable-next-line jsx-a11y/anchor-is-valid
		<a
			href="#"
			onClick={() => {
				setIsSubscriptionDialogOpen(true);
			}}
		>
			<figure>
				{contentProtection ? (
					// eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions
					<img
						src={cover}
						alt={t('common.coverOf', { name: name })}
						width={340}
						fetchPriority="high"
						className="content-protection"
						onContextMenu={(e) => e.preventDefault()}
					/>
				) : (
					<img
						src={cover}
						alt={t('common.coverOf', { name: name })}
						width={340}
						fetchPriority="high"
					/>
				)}
			</figure>
		</a>
	);

	return (
		<div className={styles['latest-issue']}>
			{imageComponent}

			<div className={styles['title']}>
				<h1 dir="auto">{name}</h1>
				<hr />
			</div>
		</div>
	);
};

// iPad mini portrait is 744px wide, under the 768px tablet breakpoint.
const LARGE_MOBILE_MIN = 600;

const Issues = ({ issues }: IssuesProps) => {
	const { isTablet, isDesktopPanel, width } = useResponsiveGridBreakpoint();
	const threeColumns = isTablet || width >= LARGE_MOBILE_MIN;

	if (isDesktopPanel) {
		// Unchanged from before virtualization -- `.issues-container` becomes
		// its own `overflow: auto` panel at this breakpoint (matches
		// basic/home/magazine/magazine.tsx's equivalent decision; see that
		// file's comment), and the grid's own CSS hides the first issue here
		// anyway (`LatestIssue` above already shows it).
		return (
			<div className={styles['issues']}>
				{issues.map((issue, i) => (
					<IssueComponent
						key={issue.id}
						issue={issue}
						isFirst={i === 0}
					/>
				))}
			</div>
		);
	}

	// Mobile (below `isTablet`'s own 768px floor) gets 2 columns, matching
	// morningstar's identical grid -- `.issue`'s mobile CSS (magazines
	// .module.css) already lays each card out as a stacked cover-over-text
	// column for exactly this, it just wasn't being given more than one
	// column to sit in.
	return <VirtualizedIssues issues={issues} columns={threeColumns ? 3 : 2} />;
};

/**
 * `improvements/perf-baselines/10-virtualization-benchmark.md`. Same
 * mechanics as `basic/home/magazine/magazine.tsx`'s equivalent, generalized
 * via `@shared/lib/hooks/use-row-virtualizer.ts` -- the one thing this
 * template needs beyond that shared piece is `featureFirstRow: true`:
 * at tablet width, magazines.module.css lays the first issue out as a
 * full-width "feature" (`grid-template-areas: 'feature feature feature'
 * '. . .'`), not just another grid cell.
 */
function VirtualizedIssues({
	issues,
	columns,
}: {
	issues: Array<Issue>;
	columns: number;
}) {
	const { containerRef, totalSize, rows, measureElement } = useRowVirtualizer(
		{ itemCount: issues.length, columns, featureFirstRow: true },
		// One issue card's rough height at this breakpoint -- a starting
		// guess only, corrected against each row's real rendered height by
		// the hook's dynamic measurement.
		{ estimateRowHeight: 420 },
	);

	return (
		<div
			className={styles['issues']}
			ref={containerRef}
			style={{
				display: 'block',
				position: 'relative',
				height: totalSize,
			}}
		>
			{rows.map((row) => {
				// The feature row is always exactly one item (index 0) --
				// everything else is a uniform `columns`-sized grid row.
				const isFeatureRow = row.itemIndices[0] === 0;
				const rowStyle: React.CSSProperties =
					!isFeatureRow && columns > 1
						? {
								display: 'grid',
								gridTemplateColumns: `repeat(${columns}, 1fr)`,
							}
						: {};
				return (
					<div
						key={row.key}
						ref={measureElement}
						{...row.rowProps}
						style={{ ...row.style, ...rowStyle }}
					>
						{row.itemIndices.map((i) => (
							<IssueComponent
								key={issues[i].id}
								issue={issues[i]}
								isFirst={i === 0}
							/>
						))}
					</div>
				);
			})}
		</div>
	);
}

interface IssuesProps {
	issues: Array<Issue>;
}

/**
 * `CoverIssue`'s `ErrorBoundary` fallback above. Rather than showing an
 * error message in the cover slot (there's nothing actionable a reader can
 * do about an offline, not-yet-cached issue from here), this clears the
 * selection and drops `id` from the URL -- the same navigation the topbar's
 * own back button performs -- so `Magazines()` re-renders straight into the
 * issues grid it was already showing a moment before, instead of stopping
 * on a dead end. `replace: true`: this is a recovery bounce, not a reader
 * action worth a history entry to step back through.
 */
function CoverIssueUnavailable() {
	const navigate = useNavigate();
	const { setSelectedIssue } = useContext(HomeContext);
	useEffect(() => {
		setSelectedIssue(null);
		navigate({
			to: '/',
			search: (prev: Record<string, unknown>) => ({
				...prev,
				id: undefined,
			}),
			replace: true,
		});
	}, [navigate, setSelectedIssue]);
	return <MagazineLoading />;
}

const CoverIssue = () => {
	const t = useT();
	const { isDigitalAccess, setIsSubscriptionDialogOpen } =
		useContext(MagazinesContext);
	const { selectedIssue } = useContext(HomeContext);
	const { issue } = useIssue(`${selectedIssue?.id}`);

	// Above the `!issue` guard -- hooks can't follow a conditional return.
	// This filter used to re-run on every render of CoverIssue, rebuilding
	// the whole TOC array (and therefore every row below it) even when the
	// issue hadn't changed.
	const filteredArticles = useMemo(
		() => (issue?.articles ? issue.articles.filter(onlyIncludedToc) : []),
		[issue?.articles],
	);

	// Greyed out offline when the reader has nothing cached for this issue
	// -- see `useOfflineCover` (`@entities/issue`). Above the `!issue`
	// guard, like `filteredArticles`.
	const offlineCover = useOfflineCover(
		issue ? (issue.slug ?? issue.id) : undefined,
		'issue',
		{ enabled: !!isDigitalAccess },
	);

	if (!issue) {
		// Not flex-centered -- same reasoning as the root `Magazines`
		// loading branch above: `MagazineLoading` is a content-shaped
		// skeleton now, and a flex-centered wrapper shrink-wraps it to its
		// own intrinsic size instead of the real width it's meant to fill.
		return (
			<div style={{ width: '100%', height: '100%', padding: 0 }}>
				<MagazineLoading />
			</div>
		);
	}
	const { id, name, cover, description, slug } = issue;

	const from = `${window.location.pathname}?section=magazines&id=${id}`;
	const onContentProtection = config?.app?.contentProtection;

	const imageComponent = isDigitalAccess ? (
		<Link
			to={`/issues/$id`}
			params={{
				id: slug ?? id,
			}}
			from={from}
			className={styles['image']}
			onClick={(event) => {
				offlineCover.blockIfUnavailable(event);
			}}
			preload={false}
		>
			{onContentProtection ? (
				// eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions
				<img
					src={cover}
					alt={t('common.coverOf', { name: name })}
					fetchPriority="high"
					className="content-protection"
					onContextMenu={(e) => e.preventDefault()}
					style={offlineCover.coverStyle}
				/>
			) : (
				<img
					src={cover}
					alt={t('common.coverOf', { name: name })}
					fetchPriority="high"
					style={offlineCover.coverStyle}
				/>
			)}
		</Link>
	) : (
		// eslint-disable-next-line jsx-a11y/anchor-is-valid
		<a
			href="#"
			onClick={() => {
				setIsSubscriptionDialogOpen(true);
			}}
		>
			<figure>
				{onContentProtection ? (
					// eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions
					<img
						src={cover}
						alt={t('common.coverOf', { name: name })}
						width={340}
						fetchPriority="high"
						className="content-protection"
						onContextMenu={(e) => e.preventDefault()}
					/>
				) : (
					<img
						src={cover}
						alt={t('common.coverOf', { name: name })}
						width={340}
						fetchPriority="high"
					/>
				)}
			</figure>
		</a>
	);
	return (
		<div className={styles['cover']}>
			<div>
				{imageComponent}
				<div className={styles['title']}>
					<h1 dir="auto">{name}</h1>
					{description ? <p dir="auto">{description}</p> : null}
					<hr />
				</div>
			</div>
			<div>
				<ul className={styles['toc']}>
					{filteredArticles.map((article) => (
						<li key={article.id}>
							<Link
								// Was `` `/issues/$id/articleId` `` -- a template
								// literal missing the `$` before `articleId`, so
								// it resolved to a literal path segment and
								// opened the reader with `articleId: "articleId"`
								// instead of the article's own id. The backticks
								// are why nothing caught it: a template literal
								// degrades to `string`, which drops TanStack
								// Router's route-literal type check. Every other
								// article link in the codebase uses this plain
								// string form.
								to="/issues/$id/$articleId"
								from={from}
								params={{
									id: id,
									articleId: article.id,
								}}
								// Cross-document continuity comes from the shared
								// Topbar's fixed `view-transition-name: topbar-nav`
								// (topbar.module.css), which both ends now carry --
								// this page since it adopted the widget, and the
								// reader via `widgets/issue-reader/responsive`.
								// TanStack only checks `typeof
								// document.startViewTransition === 'function'`; it
								// does not consult `prefers-reduced-motion`, the
								// same pre-existing gap every other
								// `viewTransition` usage in this codebase has.
								viewTransition
							>
								<h3 dir="auto">{article.name?.['en']}</h3>
								{article.description ? (
									<small dir="auto">{article.description}</small>
								) : null}
							</Link>
						</li>
					))}
					<div className={styles['offer']}>
						<a
							href="https://www.pw.org/about-us/advertise/display"
							target="_blank"
							rel="noreferrer"
						>
							<img alt={t('common.offer')} src="/assets/test.png" />
						</a>
					</div>
				</ul>
			</div>
		</div>
	);
};

/*Exclude articles that are excluded from being display in Toc*/
function onlyIncludedToc(article: Article) {
	if (article.role === 'advert') {
		return false;
	}
	// Cast: `channel` is a JSON scalar with no schema shape (see
	// constants.ts's Article doc comment); `toc` is a CMS-defined key this
	// pass doesn't reconcile a schema for.
	const channel = article.channel as { toc?: string } | null | undefined;
	return article.channel === null || channel?.toc === 'included';
}

// Memoized: one card per issue in the grid. `issue` objects come from the
// query cache and keep their identity, so a card now re-renders only when
// its own issue changes -- previously any re-render of the grid's parent
// (a context tick, a search keystroke) re-rendered every card.
//
// `isFirst` replaces this file's own `.issue:first-of-type` CSS rules
// (magazines.module.css, now `.issue[data-first-issue]`): virtualization
// (below) wraps each rendered row in its own container, which breaks
// `:first-of-type`'s real meaning -- every row's item would become "first
// of its own row-wrapper parent," not just the genuine first issue. An
// explicit `data-first-issue` attribute, set only when this really is
// index 0, doesn't depend on DOM sibling structure the way the pseudo-class
// did.
const IssueComponent = memo(function IssueComponent({
	issue,
	isFirst,
}: IssueProps) {
	const t = useT();
	const { isDigitalAccess, setIsSubscriptionDialogOpen } =
		useContext(MagazinesContext);
	const { id, name, cover, slug } = issue;
	// Was `if (!issue.description) issue.description = ''` -- a render-time
	// write to a shared object just to turn null/undefined into a string.
	// Derived instead; the rendered output is identical.
	const description = issue.description ?? '';
	const template = config.app.template as TemplatePoets;
	const skipTOC = !!template?.config?.skipTOC;
	const from = `${window.location.pathname}?section=magazine`;
	const contentProtection = config?.app?.contentProtection;
	const transitionName = previewTransitionName(slug ?? id);
	// Greyed out offline when the page this cover opens has nothing cached
	// (the reader with `skipTOC`, the preview screen without) -- see
	// `useOfflineCover` (`@entities/issue`). Not for readers without digital
	// access: their cover opens the subscription dialog, not an issue.
	const { coverStyle: coverFilterStyle, blockIfUnavailable } =
		useOfflineCover(slug ?? id, skipTOC ? 'issue' : 'preview', {
			enabled: !!isDigitalAccess,
		});
	const onClick = (event: React.MouseEvent) => {
		// No tracking event for a click that didn't go anywhere.
		blockIfUnavailable(event);
	};

	const imageComponent = isDigitalAccess ? (
		skipTOC ? (
			<PreviewLink
				to="/issues/$id"
				params={{ id: `${slug ?? id}` }}
				from={from}
				onClick={onClick}
				// Every template's covers opt out of the router's hover/
				// touchstart prefetch: it would cache issues never actually
				// opened, and "not greyed out" (`useOfflineCover`) would stop
				// meaning "readable offline". The click still fetches normally.
				preload={false}
				// The cover morphs into the reader's own cover
				// (`widgets/issue-reader`'s `IssueCover`), same as basic's
				// `skipTOC` branch.
				payload={{ preview: { imageSrc: cover, transitionName } }}
			>
				<figure>
					{contentProtection ? (
						// eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions
						<img
							loading="lazy"
							src={cover}
							alt={t('common.coverOf', { name: name })}
							className="content-protection"
							onContextMenu={(e) => e.preventDefault()}
							width={340}
							style={{
								viewTransitionName: transitionName,
								...coverFilterStyle,
							}}
						/>
					) : (
						<img
							loading="lazy"
							src={cover}
							alt={t('common.coverOf', { name: name })}
							width={340}
							style={{
								viewTransitionName: transitionName,
								...coverFilterStyle,
							}}
						/>
					)}
				</figure>
			</PreviewLink>
		) : (
			<PreviewLink
				to="/preview/$id"
				params={{ id: `${slug ?? id}` }}
				from={from}
				onClick={onClick}
				preload={false}
				payload={{ preview: { imageSrc: cover, transitionName } }}
			>
				<figure>
					{contentProtection ? (
						// eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions
						<img
							loading="lazy"
							src={cover}
							alt={t('common.coverOf', { name: name })}
							className="content-protection"
							onContextMenu={(e) => e.preventDefault()}
							width={340}
							style={{
								viewTransitionName: transitionName,
								...coverFilterStyle,
							}}
						/>
					) : (
						<img
							loading="lazy"
							src={cover}
							alt={t('common.coverOf', { name: name })}
							width={340}
							style={{
								viewTransitionName: transitionName,
								...coverFilterStyle,
							}}
						/>
					)}
				</figure>
			</PreviewLink>
		)
	) : (
		// eslint-disable-next-line jsx-a11y/anchor-is-valid
		<a
			href="#"
			onClick={() => {
				setIsSubscriptionDialogOpen(true);
			}}
		>
			<figure>
				{contentProtection ? (
					// eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions
					<img
						loading="lazy"
						src={cover}
						alt={t('common.coverOf', { name: name })}
						width={340}
						className="content-protection"
						onContextMenu={(e) => e.preventDefault()}
					/>
				) : (
					<img
						loading="lazy"
						src={cover}
						alt={t('common.coverOf', { name: name })}
						width={340}
					/>
				)}
			</figure>
		</a>
	);
	const heading = <h4 dir="auto">{name}</h4>;
	return (
		<div
			className={styles['issue']}
			data-first-issue={isFirst || undefined}
		>
			<div>{imageComponent}</div>
			<div>
				{heading}
				<p dir="auto">{description}</p>
			</div>
		</div>
	);
});

interface IssueProps {
	issue: Issue;
	isFirst?: boolean;
}

/**
 * How many placeholder issues the grid draws while loading. Enough to fill a
 * desktop viewport without implying a real count -- the actual list length is
 * unknown until the query resolves.
 */
const SKELETON_ISSUE_COUNT = 9;

// `width: '100%'` capped by `maxWidth: 340`, not the other way round: a real
// <img width={340}> is a replaced element, so CSS grid lets it shrink below
// its intrinsic width to fit a narrower `1fr` track. This placeholder is a
// plain <div> (via Skeleton) -- an ordinary block box's min-content size
// doesn't shrink the same way, so an explicit `width: 340` forced every
// track in the grid to stay at least 340px wide, blowing the whole `.issues`
// grid past its own `max-width` at desktop/tablet widths where the
// available column is narrower than that. `width: '100%'` has no such
// floor: it always resolves to whatever the track actually gives it.
//
// Applied to a plain wrapper `<div>`, not to `Skeleton` directly -- matching
// top-stories' own `FeedSkeleton` (`.image` div sized by CSS, `Skeleton`
// inside it just `w-full h-full`). `Skeleton` carries `animate-pulse`, a
// running opacity animation; putting `aspect-ratio` on that same animating
// element (the previous shape here) meant its box size was resolved by the
// grid's intrinsic-sizing pass on every frame the animation was live for,
// not once up front like a static box. Symptom matched exactly: readers
// reported the cover placeholders visibly growing and shrinking while the
// pulse ran, not just fading. A wrapper with a fixed, unanimated box
// removes the animated element from that sizing pass entirely -- it just
// fills whatever size the wrapper already settled on.
const SKELETON_COVER_STYLE = {
	aspectRatio: '9 / 12',
	width: '100%',
	maxWidth: 340,
} as const;

/**
 * Loading state for the magazines grid.
 *
 * A skeleton shaped like the real layout rather than a centred spinner
 * (morningstar's Phase 0.21). The point is not decoration: this grid is the
 * first thing a reader sees, and a spinner gives the page no height, so the
 * content lands and shoves everything down the moment it arrives. Reserving
 * the real boxes up front means the arrival is a fill, not a jump.
 *
 * Mirrors `IssuesContainer` exactly -- `latest-issue` (cover, then title) in
 * the first column and the `issues` grid in the second, with each `issue`
 * being poets' own two-column `cover | text` layout rather than
 * morningstar's stacked one. A skeleton that doesn't match its content is
 * worse than none, because the shift it causes is unexpected.
 */
const MagazineLoading = () => {
	return (
		<div className={styles['issues-container']} aria-busy="true">
			<div>
				<div className={styles['latest-issue']}>
					<figure>
						{/* `styles['skeleton-cover']` carries the same width
						    per breakpoint as `.latest-issue img`'s own three
						    rules -- a static inline style can't express
						    that, and previously left this 100-250px too
						    wide/tall against the real 380/400px-capped
						    cover at desktop widths.
						    `aspect-ratio` lives on this plain wrapper div, not
						    on `Skeleton` itself -- see `SKELETON_COVER_STYLE`'s
						    own comment below for why. */}
						<div
							className={styles['skeleton-cover']}
							style={{ aspectRatio: '9 / 12' }}
						>
							<Skeleton className="w-full h-full bg-gray-300" />
						</div>
					</figure>
					<div className={styles['title']}>
						<Skeleton className="h-6 w-2/3 mx-auto bg-gray-300" />
					</div>
				</div>
			</div>
			<div>
				<div className={styles['issues']}>
					{Array.from({ length: SKELETON_ISSUE_COUNT }).map(
						(_, i) => (
							<div key={i} className={styles['issue']}>
								{/* See `SKELETON_COVER_STYLE`'s own comment for why
							    this is `width: 100%` capped by `maxWidth: 340`,
							    not a real `.issue img`'s `width={340}` capped by
							    `max-width: 100%`. */}
								<div>
									<figure>
										<div style={SKELETON_COVER_STYLE}>
											<Skeleton className="w-full h-full bg-gray-300" />
										</div>
									</figure>
								</div>
								<div>
									<Skeleton className="h-5 w-2/4 bg-gray-300 mx-auto " />
								</div>
							</div>
						),
					)}
				</div>
			</div>
		</div>
	);
};

const SubscritionDialog: React.FC<SubscritionDialogProps> = ({
	isOpen,
}: SubscritionDialogProps) => {
	const t = useT();
	const { setIsSubscriptionDialogOpen } = useContext(MagazinesContext);
	const navbarIconUrl = useNavbarIconUrl();

	const { subscriptionUrl, title } = config.build;
	return (
		<Dialog
			open={isOpen}
			onOpenChange={(open) => {
				if (!open) {
					setIsSubscriptionDialogOpen(false);
				}
			}}
		>
			<DialogContent
				className={dialogCardClassName}
				backdropClassName={dialogCardBackdropClassName}
			>
				<div className={styles['subscription-dialog']}>
					<img src={navbarIconUrl} alt={t('common.logo')} />
					<h3>{t('common.subscriberRequired')}</h3>
					<p>
						<T
							k="common.subscribeToEdition"
							values={{ title }}
							components={{ em: (chunks) => <em>{chunks}</em> }}
						/>
					</p>
					{subscriptionUrl ? (
						<button
							type="button"
							onClick={() => {
								track('subscribe_click', { location: window.location.pathname });
								open(subscriptionUrl, 'blank');
							}}
						>
							{t('common.subscribeNow')}
						</button>
					) : null}
				</div>
			</DialogContent>
		</Dialog>
	);
};

interface SubscritionDialogProps {
	isOpen: boolean;
}
